import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState, useRef, useEffect } from "react";
import { Sun, LifeBuoy, LogOut, User, ChevronDown, Activity, Menu, X, FolderArchive, FileText } from "lucide-react";

/**
 * CustomerLayout — completely isolated from the admin Layout.
 *
 * Design rules:
 *   - No admin navigation ever renders here.
 *   - Nav is limited to My Solar, Documents, Support, Reports.
 *   - Same brand card + user menu so the customer feels part of RPPL.
 */

const CUSTOMER_NAV = [
    { to: "/customer/dashboard", label: "My Solar", icon: Sun, testid: "cust-nav-dashboard" },
    { to: "/customer/documents", label: "Documents", icon: FolderArchive, testid: "cust-nav-documents" },
    { to: "/customer/support", label: "Support", icon: LifeBuoy, testid: "cust-nav-support" },
    { to: "/customer/reports", label: "Reports", icon: FileText, testid: "cust-nav-reports" },
];

export default function CustomerLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [sideOpen, setSideOpen] = useState(false);
    const menuRef = useRef();

    useEffect(() => {
        const h = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenuOpen(false);
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    return (
        <div className="min-h-screen bg-[#F7F8FA]">
            <header
                className="fixed top-0 left-0 right-0 bg-[#0B0D11]"
                style={{ height: "72px", borderBottom: "1px solid rgba(255,255,255,0.05)", zIndex: 1100 }}
                data-testid="customer-header"
            >
                <div className="h-full flex items-center gap-4 px-4 sm:px-6">
                    <button
                        onClick={() => setSideOpen(v => !v)}
                        className="lg:hidden w-10 h-10 rounded-md flex items-center justify-center text-white hover:bg-white/5"
                        data-testid="cust-sidebar-toggle"
                        aria-label="Toggle menu"
                    >
                        {sideOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>

                    <Link
                        to="/customer/dashboard"
                        className="flex-shrink-0 flex items-center transition-transform duration-200 hover:scale-[1.01]"
                        style={{
                            width: "280px", height: "68px", borderRadius: "14px",
                            background: "linear-gradient(135deg, #181818 0%, #0E0E0E 100%)",
                            border: "1px solid rgba(255,255,255,0.06)",
                            boxShadow: "0 6px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.03)",
                            paddingLeft: "18px", paddingRight: "18px",
                        }}
                        data-testid="cust-brand-card"
                    >
                        <div
                            className="flex items-center justify-center bg-white"
                            style={{ height: "52px", width: "100%", padding: "6px 14px", borderRadius: "10px" }}
                        >
                            <img src="/rppl-logo.png" alt="RPPL Solar" style={{ height: "44px", width: "auto", maxWidth: "100%", objectFit: "contain" }} />
                        </div>
                    </Link>

                    <div className="flex-1" />

                    <div className="hidden lg:flex items-center gap-2 px-3 h-8 rounded-full text-[11px] font-mono"
                        style={{ background: "rgba(22,163,74,0.10)", border: "1px solid rgba(22,163,74,0.25)", color: "#16A34A" }}
                        data-testid="cust-header-live"
                    >
                        <Activity className="w-3 h-3" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" />
                        LIVE · My Solar
                    </div>

                    <div className="relative" ref={menuRef}>
                        <button onClick={() => setMenuOpen(v => !v)} className="flex items-center gap-2.5 pl-2 pr-3 h-11 rounded-lg hover:bg-white/5 transition-colors duration-200" data-testid="cust-user-menu-trigger">
                            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "#0A0A0A20", border: "1px solid #0A0A0A40" }}>
                                <User className="w-4 h-4" style={{ color: "#0A0A0A" }} />
                            </div>
                            <div className="hidden sm:block text-left leading-tight">
                                <div className="text-xs text-white font-medium truncate max-w-[140px]">{user?.name}</div>
                                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#0A0A0A" }}>Customer</div>
                            </div>
                            <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-2 w-72 overflow-hidden"
                                style={{ background: "linear-gradient(135deg, #181818 0%, #0E0E0E 100%)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", boxShadow: "0 12px 32px rgba(0,0,0,0.35)" }}
                                data-testid="cust-user-menu"
                            >
                                <div className="p-4 border-b border-white/5">
                                    <div className="text-sm font-medium text-white">{user?.name}</div>
                                    <div className="text-xs text-zinc-500 font-mono truncate mt-0.5">{user?.email}</div>
                                    <div className="text-[10px] uppercase tracking-widest mt-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
                                        style={{ background: "#0A0A0A20", color: "#0A0A0A", border: "1px solid #0A0A0A44" }}>
                                        <span className="w-1 h-1 rounded-full" style={{ background: "#0A0A0A" }} />
                                        Customer
                                    </div>
                                </div>
                                <NavLink to="/customer/profile" onClick={() => setMenuOpen(false)} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 hover:text-[#0A0A0A] transition-colors duration-200 border-b border-white/5" data-testid="cust-menu-profile-link">
                                    <User className="w-4 h-4" /> My Profile
                                </NavLink>
                                <button onClick={() => { logout(); nav("/login"); }} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 hover:text-[#0A0A0A] transition-colors duration-200" data-testid="cust-logout-btn">
                                    <LogOut className="w-4 h-4" /> Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {sideOpen && (
                <div className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm" style={{ top: "72px", zIndex: 1000 }} onClick={() => setSideOpen(false)} />
            )}

            <aside
                className={`fixed left-0 bg-[#0B0D11] overflow-y-auto transition-transform duration-200 ${sideOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
                style={{
                    top: "72px",
                    width: "240px",
                    height: "calc(100vh - 72px)",
                    borderRight: "1px solid rgba(255,255,255,0.05)",
                    zIndex: 1050,
                }}
                data-testid="customer-sidebar"
            >
                <nav className="p-3 space-y-6">
                    <div>
                        <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-zinc-500 px-3 mb-2">
                            My Portal
                        </div>
                        <div className="space-y-0.5">
                            {CUSTOMER_NAV.map(n => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    onClick={() => setSideOpen(false)}
                                    data-testid={n.testid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                                            isActive
                                                ? "bg-[#0A0A0A] text-white font-medium shadow-[0_2px_8px_rgba(249,115,22,0.35)]"
                                                : "text-[#C7CBD2] hover:bg-white/[0.08] hover:text-white"
                                        }`
                                    }
                                >
                                    <n.icon className="w-4 h-4 flex-shrink-0" />
                                    {n.label}
                                </NavLink>
                            ))}
                        </div>
                    </div>

                    <div className="px-3 py-4 rounded-md bg-white/[0.03] border border-white/[0.06]">
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-[#16A34A] mb-1">Need help?</div>
                        <div className="text-[11px] text-zinc-400 leading-relaxed">
                            Raise a ticket from any plant page and our engineers will get on it.
                        </div>
                    </div>
                </nav>
            </aside>

            <main
                data-testid="customer-main"
                className="pl-0 lg:pl-[240px]"
                style={{ paddingTop: "104px" }}
            >
                <div className="px-4 sm:px-6 lg:px-8 pb-10 space-y-6" style={{ maxWidth: "1600px" }}>
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
